import { Outlet, NavLink, useLocation, Link } from "react-router-dom";
import { useState } from "react";
import {
  Activity, ChartColumnIncreasing, Eye, Gem, History, LayoutDashboard, LayoutGrid,
  MessageSquare, PanelLeft, Search, Settings, ShieldPlus, Store, Trophy, Volleyball,
} from "lucide-react";
import { useDemo } from "../context/DemoContext";
import { useAuth } from "../context/AuthContext";
import { useRealData } from "../lib/real-store";

const navItems = [
  { path: "/dashboard", label: "Visão geral", Icon: LayoutDashboard },
  { path: "/chat", label: "Chat", Icon: MessageSquare },
  { path: "/analysis", label: "Análise de Mercado", Icon: ChartColumnIncreasing },
  { path: "/partidas", label: "Partidas", Icon: Volleyball },
  { path: "/campeonatos", label: "Campeonatos", Icon: Trophy },
  { path: "/times", label: "Times", Icon: ShieldPlus },
  { path: "/insights", label: "Insights", Icon: Eye },
  { path: "/value-odds", label: "Odds de valor", Icon: Gem },
  { path: "/search", label: "Busca avançada", Icon: Search },
  { path: "/history", label: "Histórico", Icon: History },
  { path: "/bookmakers", label: "Minhas Casas", Icon: Store },
  { path: "/settings", label: "Configurações", Icon: Settings },
];

function Brand() {
  return <NavLink to="/dashboard" className="brand" aria-label="Tracker Bet Analystic — Início">
    <span className="brand-mark" aria-hidden="true"><Activity size={18} strokeWidth={1.75} /></span>
    <span>Tracker Bet<small>Analystic</small></span>
  </NavLink>;
}

export function Layout() {
  const { isDemo, toggleDemo } = useDemo();
  const { configured, user, identity, signOut } = useAuth();
  const { analyses } = useRealData();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const page = navItems.find(item => item.path === location.pathname)?.label
    ?? (location.pathname === "/coverage" ? "Cobertura"
      : location.pathname.startsWith("/partidas/odds-de-valor") ? "Odds de valor"
      : location.pathname.startsWith("/match/") ? "Partida"
      : location.pathname.startsWith("/report/") ? "Relatório"
      : "Tracker Bet Analystic");
  // P01 — diagnóstico de cobertura: visível apenas para o proprietário do espaço.
  const visibleItems = identity?.role === "owner"
    ? [...navItems, { path: "/coverage", label: "Cobertura", Icon: LayoutGrid }]
    : navItems;
  const links = visibleItems.map(({ path, label, Icon }) =>
    <NavLink key={path} to={path} className={({ isActive }) => `nav-link${isActive ? " active" : ""}`}>
      <span className="nav-icon" aria-hidden="true"><Icon size={19} strokeWidth={1.75} /></span>{label}
    </NavLink>
  );

  return (
    <div className={`app-shell app-shell-reference${collapsed ? " sidebar-collapsed" : ""}`}>
      <a href="#main-content" className="skip-link">Pular para o conteúdo</a>
      <aside className="app-sidebar">
        <div className="reference-sidebar-brand">
          <Brand />
          <button className="sidebar-collapse-button" type="button" onClick={() => setCollapsed(value => !value)} aria-label={collapsed ? "Expandir navegação" : "Recolher navegação"} aria-expanded={!collapsed}><PanelLeft size={17} strokeWidth={1.75} /></button>
        </div>
        <p className="eyebrow px-3 mb-3 reference-sidebar-label">ANÁLISE ESPORTIVA</p>
        <nav aria-label="Navegação principal">{links}</nav>
        <div className="reference-recents">
          <p className="eyebrow">RECENTES</p>
          {analyses.filter((item) => item.status === "completed").slice(0, 4).map((item, index) => (
            <Link key={item.id} to={`/report/${item.id}`} className={`reference-recent-link${index === 0 ? " is-current" : ""}`} title={`${item.homeTeamName} x ${item.awayTeamName}`}>
              <span className="reference-recent-dot" aria-hidden="true" /><span>{item.homeTeamName} x {item.awayTeamName}</span>
            </Link>
          ))}
          {!analyses.some((item) => item.status === "completed") && <span className="reference-recents-empty">Suas análises aparecerão aqui</span>}
        </div>
        <p className="sidebar-note">Dados, contexto e transparência.</p>
        {configured && user && <div className="reference-user-card"><span className="reference-user-avatar">{(identity?.displayName ?? user.email ?? "T").slice(0, 1).toUpperCase()}</span><span><strong>{identity?.displayName ?? user.email}</strong><small>{identity?.workspaceName ?? "Workspace"}</small></span></div>}
      </aside>
      <div className="app-body">
        <header className="app-topbar">
          <div className="mobile-brand"><Brand /></div>
          <div className="hidden md:block">
            <p className="eyebrow">{identity?.workspaceName ?? "Tracker Bet Analystic"}</p>
            <p className="text-sm mt-1">{page}</p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/partidas" className="btn btn-primary btn-sm reference-top-action">Nova análise</Link>
            {configured && user ? <>
              <button onClick={() => void signOut()} className="btn btn-secondary btn-sm" aria-label="Sair da conta">Sair</button>
            </> : <>
              {isDemo && <span className="badge badge-demo hidden sm:inline-flex">Demo</span>}
              <button onClick={toggleDemo} className="btn btn-secondary btn-sm"
                aria-label={isDemo ? "Sair do modo demonstração" : "Entrar no modo demonstração"}>
                {isDemo ? "Sair Demo" : "Modo Demo"}
              </button>
            </>}
          </div>
        </header>
        <nav className="mobile-nav" aria-label="Navegação móvel">{links}</nav>
        <main id="main-content" className="app-main reference-main" tabIndex={-1}>
          {isDemo && <div className="demo-banner" role="status">
            <span className="badge badge-demo">Demonstração</span>
            <div className="demo-banner-content">
              <p className="demo-banner-text">
                Dados sintéticos para explorar a interface. Não use para decisões reais.
                {configured && " Autenticação e isolamento usam sua conta Supabase."}
              </p>
            </div>
          </div>}
          <Outlet />
        </main>
        <footer className="app-footer">
          Tracker Bet Analystic · Resultados anteriores não garantem os próximos.
        </footer>
      </div>
    </div>
  );
}
